/**
 * Operations API client (P1 운영 관제 센터).
 *
 * /api/operations/overview 단일 호출로 시스템 상태 + 시장 + 백업 + Telegram + 24h 집계를 받는다.
 */
import { apiFetch } from '../utils/apiFetch';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001';

export interface TradingStatus {
  trading_mode: string;
  paper_trading: boolean;
  live_trading_enabled: boolean;
  order_guard: string;
  stock_live_route?: 'toss' | 'alpaca_paper';
  stock_live_route_when_live?: 'toss' | 'blocked';
  toss_live_market?: 'US' | 'KR';
  toss_us_live_enabled?: boolean;
  toss_kr_live_enabled?: boolean;
}

export interface MarketState {
  is_open: boolean;
  status: 'open' | 'closed_regular' | 'closed_holiday' | 'unknown';
  next_open: string | null;
  next_close: string | null;
  timestamp: string;
  source: 'alpaca' | 'cache' | 'fallback';
}

export interface SchedulerState {
  last_execution_at: string | null;
  watchlist_last_refresh_at: string | null;
  watchlist_count: number;
  latest_execution_log_at: string | null;
}

export interface StrategySignalHealth {
  status: 'ok' | 'warn' | 'bad' | 'no_data';
  window_minutes: number;
  total: number;
  failure_count: number;
  failure_rate: number;
  latest_failure_at: string | null;
  latest_failure_symbols: string[];
  latest_error: string | null;
}

export interface BrokerSyncState {
  positions_count: number;
  long_positions_count: number;
  unsynced_count: number;
  latest_broker_sync_at: string | null;
}

export interface BackupStatus {
  status: 'ok' | 'stale' | 'failed' | 'not_configured' | 'read_error' | 'unknown';
  message: string;
  file: string | null;
  size_bytes: number;
  created_at: string | null;
  age_seconds: number | null;
}

export interface TelegramChannel {
  channel: 'trade_alerts' | 'daily_report' | 'ops_alerts';
  status: 'configured' | 'not_configured' | 'not_implemented';
  last_sent_at: string | null;
  note: string;
}

export interface TelegramStatus {
  is_configured: boolean;
  channels: TelegramChannel[];
}

export interface DecisionLogItem {
  id: number;
  symbol: string;
  timestamp: string | null;
  action: string;
  executed: boolean;
  reject_reason?: string | null;
}

export interface ExecutionLogItem {
  id: number;
  symbol: string;
  executed_at: string | null;
  action: string;
  executed: boolean;
  reason: string;
}

export interface OperationsOverview {
  generated_at: string;
  window_hours: number;
  trading: TradingStatus;
  market: MarketState;
  health: Record<string, { healthy: boolean; error?: string }>;
  scheduler: SchedulerState;
  strategy_signal_health?: StrategySignalHealth;
  broker_sync: BrokerSyncState;
  decisions: {
    total: number;
    by_action: Record<string, number>;
    executed_count: number;
    rejected_count: number;
    latest: DecisionLogItem[];
  };
  execution_logs: {
    total: number;
    by_action: Record<string, number>;
    executed_count: number;
    latest: ExecutionLogItem[];
  };
  server_logs: {
    source: string;
    recent_issue_logs: ExecutionLogItem[];
    recent_rejections: DecisionLogItem[];
  };
  backup: BackupStatus;
  telegram: TelegramStatus;
  live_pilot_guards?: LivePilotGuards;
  analyst_shadow?: AnalystShadowSummary;
  watchlist_analysis?: WatchlistAnalysis;
}

export interface AnalystShadowSummary {
  version: string;
  mode: 'agent_shadow';
  lookback_hours: number;
  rule_decision_count: number;
  shadow_decision_count: number;
  pending_count: number;
  auto?: AnalystShadowAutoStatus;
  agreement: Record<'agree' | 'disagree' | 'mixed' | 'unknown', number>;
  analysis?: AnalystShadowAnalysis;
  latest: AnalystShadowItem[];
  non_goals: string[];
  status?: 'saved' | 'no_candidates' | 'daily_cap_reached' | 'error';
  saved_count?: number;
  error?: string;
}

export interface AnalystShadowAutoStatus {
  enabled: boolean;
  lookback_hours: number;
  batch_size: number;
  daily_row_limit: number;
  today_processed_count: number;
  today_remaining: number;
  last_shadow_at: string | null;
}

export interface AnalystShadowAnalysis {
  action_matrix: AnalystShadowActionMatrix[];
  outcome_status: string;
  note: string;
}

export interface AnalystShadowActionMatrix {
  rule_action: string;
  analyst_action: string;
  count: number;
}

export interface AnalystShadowItem {
  id: number;
  symbol: string;
  timestamp: string | null;
  action: string;
  confidence: number;
  rule_decision_id?: number | null;
  rule_action?: string | null;
  agreement?: string | null;
}

export interface LivePilotGuards {
  mode: string;
  active: boolean;
  live_trading_enabled: boolean;
  live_orders: 'enabled' | 'locked';
  active_profile: RuntimeRiskProfile;
  standby_profile: LivePilotProfile;
  profile: LivePilotProfile;
  guards: {
    daily_loss: LivePilotDailyLossGuard;
    sector_limit: LivePilotSectorGuard;
    cooldown: LivePilotCooldownGuard;
  };
}

export interface RuntimeRiskProfile {
  name: string;
  source?: string;
  max_positions?: number;
  position_size_percent?: number;
  min_order_percent?: number;
  max_trades_per_day?: number;
  buy_threshold?: number;
  sell_threshold?: number;
  use_dynamic_position_size?: boolean;
  min_position_percent?: number;
  max_position_percent?: number;
}

export interface LivePilotProfile {
    name: string;
    max_positions: number;
    position_size_percent: number;
    max_trades_per_day: number;
    buy_threshold: number;
    max_positions_per_sector: number;
    daily_loss_limit_percent: number;
    loss_cooldown_days: number;
    applies_when?: string;
    currently_applied?: boolean;
}

export interface LivePilotDailyLossGuard {
  status: 'standby' | 'pass' | 'fail' | 'unknown';
  limit_percent: number;
  daily_return_percent?: number | null;
  equity?: number | null;
  last_equity?: number | null;
  error?: string;
}

export interface LivePilotSectorGuard {
  status: 'standby' | 'pass' | 'fail' | 'unknown';
  max_per_sector: number;
  position_count?: number;
  exposure?: Record<string, number>;
  error?: string;
}

export interface LivePilotCooldownGuard {
  status: 'standby' | 'pass' | 'fail' | 'unknown';
  cooldown_days: number;
  recent_loss_symbols: string[];
  recent_loss_count: number;
}

export interface WatchlistAnalysis {
  generated_at: string;
  mode: 'shadow_only';
  method?: {
    version: string;
    max_symbols: number;
    analysis_cache_ttl_seconds: number;
    bars_lookback_days: number;
    recent_loss_window_days: number;
    volatility_target_atr_percent: number;
  };
  watchlist_count: number;
  position_count: number;
  last_refresh_at: string | null;
  freshness?: WatchlistFreshness;
  sector_counts: Record<string, number>;
  evidence_summary?: EvidenceSummary;
  summary: {
    scored_count: number;
    top_watchlist_overlap: number;
    watchlist_count: number;
    position_count: number;
  };
  symbols: WatchlistSymbol[];
}

export interface EvidenceSummary {
  recommendations_count?: number;
  scored_count?: number;
  average_score?: number | null;
  low_evidence_count?: number;
  high_severity_count?: number;
  not_evaluated_count?: number;
  flagged_symbols?: EvidenceFlaggedSymbol[];
}

export interface EvidenceFlaggedSymbol {
  symbol: string;
  severity?: 'low' | 'medium' | 'high' | null;
  flags: string[];
}

export interface WatchlistFreshness {
  screening?: RefreshMetadata;
  last_refresh_status?: RefreshMetadata;
  usage?: WatchlistGeminiUsage;
}

export interface RefreshMetadata {
  generated_at?: string;
  status?: string;
  rag_mode?: string;
  finnhub_enabled?: boolean | null;
  fallback_used?: boolean;
  fallback_type?: string | null;
  refresh_error?: string | null;
  news_window_days?: number;
  candidate_universe_count?: number;
  max_headlines_per_symbol?: number;
  news_symbol_count?: number;
  prompt_symbol_count?: number;
  prompt_symbol_limit?: number;
  model?: string | null;
  usage?: GeminiTokenUsage;
}

export interface WatchlistGeminiUsage {
  date?: string;
  auto_calls?: number;
  manual_calls?: number;
  total_calls?: number;
  auto_daily_limit?: number;
  total_daily_limit?: number;
  completed_auto_slots?: string[];
  scheduled_slots_et?: string[];
  next_slot_et?: string | null;
  last_call_at?: string | null;
  last_refresh_type?: string | null;
  last_usage?: GeminiTokenUsage;
}

export interface GeminiTokenUsage {
  model?: string;
  prompt_token_count?: number | null;
  candidates_token_count?: number | null;
  total_token_count?: number | null;
  estimated_cost_usd?: number | null;
}

export interface WatchlistSymbol {
  symbol: string;
  status: 'watchlist' | 'holding' | 'watchlist_holding';
  rank: number | null;
  source: string;
  sector: string | null;
  rationale: {
    why_now: string | null;
    catalyst: string | null;
    reason: string | null;
    source_hint: string | null;
  };
  evidence?: RecommendationEvidence | null;
  quant: {
    score: number | null;
    data_quality: string;
    metrics: Record<string, number>;
    scores?: Record<string, number>;
    flags: string[];
  };
}

export interface RecommendationEvidence {
  rationale_score: number | null;
  rationale_tier_a: boolean[];
  rationale_tier_b: boolean[];
  rationale_tier_c_skipped: boolean;
  evidence_flags: string[];
  evidence_severity: 'low' | 'medium' | 'high';
  evidence_status?: 'evaluated' | 'not_evaluated';
  evidence_evaluated_at: string;
}

export interface DailyTacticalReadiness {
  mode: string;
  stage: string;
  official_cohort_id: string;
  rehearsal_cohort_id: string;
  plumbing_cohort_id: string;
  official_stage_clock_started: boolean;
  can_start_official_stage_a: boolean;
  blockers: string[];
  flags: {
    scheduler_enabled: boolean;
    real_model_enabled: boolean;
    cohort_mode: string;
  };
  cohorts: {
    official_run_count: number;
    rehearsal_run_count: number;
    plumbing_run_count: number;
    recent_run_count: number;
  };
  model_audit: {
    latest_recorded_model_name: string | null;
    uses_fallback_model_name: boolean;
    temperature_zero_configured: boolean;
    pinned_model_recorded_on_run: boolean;
  };
  model_output_audit?: {
    checked_run_count: number;
    status_counts: Record<string, number>;
    fallback_or_unrecorded_count: number;
    fallback_or_unrecorded_run_ids: string[];
  };
  deadline_audit?: {
    checked_run_count: number;
    late_deviation_count: number;
    late_run_ids: string[];
  };
  evidence_completeness?: DailyTacticalEvidenceCompleteness[];
  audit_readiness: Record<string, boolean>;
  live_order_safety: {
    live_broker_orders_count: number;
    broker_submit_allowed: boolean;
    real_capital_allowed: boolean;
  };
}

export interface DailyTacticalEvidenceCompleteness {
  run_id: string;
  trading_date: string;
  cohort_id: string;
  source_types: string[];
  missing_required_source_types: string[];
  has_news_or_premarket: boolean;
  complete: boolean;
}

export interface MarketStatusSimple {
  is_open: boolean;
  current_time_et: string;
  status_text: string;
  next_event: string;
  hours_until_next: number | null;
}

export interface DailyTacticalAuditSection {
  implemented: boolean;
  passed: boolean;
  rows: unknown[];
  note?: string;
}

export interface DailyTacticalWeeklyAudit {
  mode: string;
  stage: string;
  cohort_id: string;
  sample_run_count: number;
  broker_order_submit_count: number;
  real_capital: number;
  live_broker_orders_count: number;
  storage_integrity_audit: DailyTacticalAuditSection;
  model_rerun_audit: DailyTacticalAuditSection;
  fact_check_audit: DailyTacticalAuditSection;
}

export interface DailyTacticalOpsReport {
  mode: string;
  stage: string;
  cohort_id: string;
  run: null | {
    run_id: string;
    trading_date: string;
    status: string;
    model_output_status: string;
    deadline_deviation: boolean;
    market_payload_coverage: {
      complete: boolean;
      market_row_count: number;
      usable_open_count: number;
      usable_ohlc_next_open_count: number;
    };
  };
  decision_distribution: Record<string, number>;
  readiness_blockers: string[];
  weekly_audit_passed: Record<string, boolean>;
  live_order_safety: DailyTacticalReadiness['live_order_safety'];
  ops_actions_required: string[];
  broker_order_submit_count: number;
  real_capital: number;
  ready_for_stage_a_daily_ops: boolean;
}

export interface DailyTacticalWeeklyReview {
  mode: string;
  stage: string;
  cohort_id: string;
  reviewed_run_count: number;
  action_counts: Record<string, number>;
  issue_counts: Record<string, number>;
  improvement_backlog: string[];
  broker_order_submit_count: number;
  real_capital: number;
}

export interface DailyTacticalBlockerAlert {
  mode: string;
  stage: string;
  cohort_id: string;
  alert_required: boolean;
  severity: 'clear' | 'warning' | 'critical';
  blockers: string[];
  message: string;
  broker_order_submit_count: number;
  real_capital: number;
}

export interface DailyTacticalReportArtifact {
  id: string;
  cohort_id: string;
  trading_date: string;
  artifact_type: string;
  payload: Record<string, unknown>;
  created_at: string;
}

export interface DailyTacticalReportHistory {
  items: DailyTacticalReportArtifact[];
}

export interface DailyTacticalImprovementProposal {
  id: string;
  cohort_id: string;
  source_artifact_id: string;
  proposal_key: string;
  title: string;
  rationale: string;
  status: string;
  validation_plan: string;
  created_at: string;
}

export interface DailyTacticalImprovementProposals {
  items: DailyTacticalImprovementProposal[];
}

export async function fetchOperationsOverview(
  hours = 24,
  limit = 5,
): Promise<OperationsOverview> {
  const url = `${API_BASE_URL}/api/operations/overview?hours=${hours}&limit=${limit}`;
  const response = await apiFetch(url);
  if (!response.ok) {
    throw new Error(`운영 관제 overview 조회 실패: ${response.status}`);
  }
  return response.json();
}

export async function runAnalystShadow(limit = 5, hours = 24): Promise<AnalystShadowSummary> {
  const url = `${API_BASE_URL}/api/operations/analyst-shadow/run?limit=${limit}&hours=${hours}`;
  const response = await apiFetch(url, { method: 'POST' });
  if (!response.ok) {
    throw new Error(`Analyst shadow 실행 실패: ${response.status}`);
  }
  return response.json();
}

export async function fetchAnalystShadowSummary(hours = 24): Promise<AnalystShadowSummary> {
  const url = `${API_BASE_URL}/api/operations/analyst-shadow?hours=${hours}`;
  const response = await apiFetch(url);
  if (!response.ok) {
    throw new Error(`Analyst shadow summary 조회 실패: ${response.status}`);
  }
  return response.json();
}

export async function fetchDailyTacticalReadiness(): Promise<DailyTacticalReadiness> {
  const url = `${API_BASE_URL}/api/operations/daily-tactical-shadow/readiness`;
  const response = await apiFetch(url);
  if (!response.ok) {
    throw new Error(`Daily Tactical readiness 조회 실패: ${response.status}`);
  }
  return response.json();
}

export async function fetchDailyTacticalWeeklyAudit(
  cohortId: string,
  sampleSize = 5,
): Promise<DailyTacticalWeeklyAudit> {
  const params = new URLSearchParams({
    cohort_id: cohortId,
    sample_size: String(sampleSize),
  });
  const url = `${API_BASE_URL}/api/operations/daily-tactical-shadow/weekly-audit?${params}`;
  const response = await apiFetch(url);
  if (!response.ok) {
    throw new Error(`Daily Tactical weekly audit 조회 실패: ${response.status}`);
  }
  return response.json();
}

export async function fetchDailyTacticalOpsReport(
  cohortId: string,
): Promise<DailyTacticalOpsReport> {
  const params = new URLSearchParams({ cohort_id: cohortId });
  const url = `${API_BASE_URL}/api/operations/daily-tactical-shadow/ops-report?${params}`;
  const response = await apiFetch(url);
  if (!response.ok) {
    throw new Error(`Daily Tactical ops report 조회 실패: ${response.status}`);
  }
  return response.json();
}

export async function fetchDailyTacticalWeeklyReview(
  cohortId: string,
): Promise<DailyTacticalWeeklyReview> {
  const params = new URLSearchParams({ cohort_id: cohortId });
  const url = `${API_BASE_URL}/api/operations/daily-tactical-shadow/weekly-review?${params}`;
  const response = await apiFetch(url);
  if (!response.ok) {
    throw new Error(`Daily Tactical weekly review 조회 실패: ${response.status}`);
  }
  return response.json();
}

export async function fetchDailyTacticalBlockerAlert(
  cohortId: string,
): Promise<DailyTacticalBlockerAlert> {
  const params = new URLSearchParams({ cohort_id: cohortId });
  const url = `${API_BASE_URL}/api/operations/daily-tactical-shadow/blocker-alert?${params}`;
  const response = await apiFetch(url);
  if (!response.ok) {
    throw new Error(`Daily Tactical blocker alert 조회 실패: ${response.status}`);
  }
  return response.json();
}

export async function fetchDailyTacticalOpsReportHistory(
  cohortId: string,
  limit = 20,
): Promise<DailyTacticalReportHistory> {
  const params = new URLSearchParams({ cohort_id: cohortId, limit: String(limit) });
  const url = `${API_BASE_URL}/api/operations/daily-tactical-shadow/ops-report-history?${params}`;
  const response = await apiFetch(url);
  if (!response.ok) {
    throw new Error(`Daily Tactical ops report history 조회 실패: ${response.status}`);
  }
  return response.json();
}

export async function fetchDailyTacticalImprovementProposals(
  cohortId: string,
): Promise<DailyTacticalImprovementProposals> {
  const params = new URLSearchParams({ cohort_id: cohortId });
  const url = `${API_BASE_URL}/api/operations/daily-tactical-shadow/improvement-proposals?${params}`;
  const response = await apiFetch(url);
  if (!response.ok) {
    throw new Error(`Daily Tactical improvement proposals 조회 실패: ${response.status}`);
  }
  return response.json();
}

export async function fetchMarketStatusSimple(): Promise<MarketStatusSimple> {
  const url = `${API_BASE_URL}/api/market/status`;
  const response = await apiFetch(url);
  if (!response.ok) {
    throw new Error(`시장 상태 조회 실패: ${response.status}`);
  }
  return response.json();
}
